<template>
    <div class="order-header" :class="{ 'multicart-mode': mode === 'multicart', 'packlist-mode': mode === 'packlist' }">
        <!-- Multicart mode header items -->
        <div v-if="mode === 'multicart'" class="header-items-section">
            <div class="header-items-group">
                <BackButton layout="tablet" class="back-button-tablet-container" />
                <SlotItem
                    :currentSlot="currentSlot"
                    :currentOrderId="currentOrder.orderId"
                />

                <ScannerItem
                    :isPrintable="isPrintable"
                    :itemsToScan="itemsToScan"
                    :scannedItems="scannedItems"
                    :isScanning="isScanning"
                />

                <!-- Mobile Workflow Badge -->
                <div v-if="isMobileWorkflowActive" class="mobile-workflow-badge">
                    <v-chip color="blue" dark small>
                        <v-icon small left>mdi-cellphone</v-icon>
                        {{ mobileWorkflowStatusText }}
                    </v-chip>
                </div>

                <OrderInfoItem
                    :user="user"
                    :orderId="currentOrder.orderId"
                    :customerAddress="currentOrder.customer.adress"
                    :customerName="currentOrder.customer.firstName + ' ' + currentOrder.customer.lastName"
                    :createDate="currentOrder.createDate"
                    :currentPackageWeight="currentPackageWeight"
                    :country="currentOrder.customer.country"
                    :openProblemModal="openProblemModal"
                    :openResetDialog="openResetDialog"
                />
            </div>
        </div>
        
        <!-- Packlist mode - PacklistItems next to header info -->
        <div v-if="mode === 'packlist'" class="packlist-header-layout">
            <!-- PacklistItems on the left -->
            <div class="packlist-items-section">
                <PacklistItems
                    :currentOrderId="currentOrderId"
                    :openItems="openItems"
                    :packedItems="packedItems"
                    :pausedItems="pausedItems"
                    :isVertical="true"
                    @item-click="handlePacklistItemClick"
                />
            </div>
            
            <!-- Header info on the right -->
            <div class="packlist-info-section">
                <div class="header-items-group">
                    <BackButton layout="tablet" class="back-button-tablet-container" />
                    
                    <!-- Product Count Badge -->
                    <ProductCountItem
                        :productsCount="productsCount"
                        :hasMissingItems="hasMissingItems"
                        :isMulticart="isMulticart"
                        :isWarranty="isWarranty"
                    />
                    
                    <OrderInfoItem
                        :user="user"
                        :orderId="currentOrder.orderId"
                        :customerAddress="currentOrder.customer.adress"
                        :customerName="currentOrder.customer.firstName + ' ' + currentOrder.customer.lastName"
                        :createDate="currentOrder.createDate"
                        :currentPackageWeight="currentPackageWeight"
                        :country="currentOrder.customer.country"
                        :openProblemModal="openProblemModal"
                        :openResetDialog="openResetDialog"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import PickupTimerItem from './items/PickupTimerItem.vue';
import SlotItem from './items/SlotItem.vue';
import OrderInfoItem from './items/OrderInfoItem.vue';
import ScannerItem from './items/ScannerItem.vue';
import CartonItem from './items/CartonItem.vue';
import ProductCountItem from './items/ProductCountItem.vue';
import PacklistItems from '../packlist/components/PacklistItems.vue';
import BackButton from './BackButton.vue';

export default {
    name: 'OrderHeader',
    components: {
        PickupTimerItem,
        SlotItem,
        ScannerItem,
        OrderInfoItem,
        CartonItem,
        ProductCountItem,
        PacklistItems,
        BackButton
    },
    props: {
        // Mode selector
        mode: {
            type: String,
            default: 'multicart',
            validator: value => ['packlist', 'multicart'].includes(value)
        },
        
        // Multicart-specific props
        currentSlot: {
            type: String,
            default: null
        },
        itemsToScan: {
            type: Number,
            default: 0
        },
        scannedItems: {
            type: Number,
            default: 0
        },
        isPrintable: {
            type: Boolean,
            default: false
        },
        isScanning: {
            type: Boolean,
            default: false
        },
        
        // Packlist-specific props
        currentOrderId: {
            type: String,
            default: null
        },
        openItems: {
            type: Array,
            default: () => []
        },
        packedItems: {
            type: Array,
            default: () => []
        },
        pausedItems: {
            type: Array,
            default: () => []
        },
        productsCount: {
            type: Number,
            default: 0
        },
        hasMissingItems: {
            type: Boolean,
            default: false
        },
        isMulticart: {
            type: Boolean,
            default: false
        },
        isWarranty: {
            type: Boolean,
            default: false
        },
        
        // Shared props
        currentOrder: {
            type: Object,
            required: true
        },
        currentPrinter: {
            type: Object,
            default: null
        },
        currentPackageWeight: {
            type: Number,
            default: 0
        },
        user: {
            type: Object,
            default: null
        },

        // Mobile workflow props
        isMobileWorkflowActive: {
            type: Boolean,
            default: false
        },
        mobileWorkflowStatusText: {
            type: String,
            default: ''
        }
    },
    watch: {
        isMobileWorkflowActive(newVal) {
            console.log('[OrderHeader] isMobileWorkflowActive changed:', newVal);
        },
        mobileWorkflowStatusText(newVal) {
            console.log('[OrderHeader] mobileWorkflowStatusText changed:', newVal);
        }
    },
    methods: {
        // Printer selection now handled through settings modal
        openProblemModal() {
            this.$emit('open-problem-modal');
        },
        openResetDialog() {
            this.$emit('open-reset-dialog');
        },
        handlePacklistItemClick(item) {
            this.$emit('item-click', item);
        }
    },
}
</script>

<style>
/* Shared styles for both modes */
.header-items-group {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

/* Mobile Workflow Badge */
.mobile-workflow-badge {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px 0;
}

/* Multicart mode styling */
.order-header.multicart-mode {
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 16px;
    justify-content: space-between;
    overflow-y: auto; /* Allow header to scroll if needed */
    box-sizing: border-box;
}

.multicart-mode .header-items-section {
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: space-between;
}

/* Packlist mode styling */
.order-header.packlist-mode {
    flex-shrink: 0;
    display: flex;
    background-color: #f9f9f9;
    border-bottom: 1px solid #e0e0e0;
    box-sizing: border-box;
}

.packlist-header-layout {
    display: flex;
    width: 100%;
    height: 100%;
}

.packlist-items-section {
    flex: 0 0 auto; /* Width based on content */
    height: 100%;
    background-color: #f9f9f9;
    border-right: 1px solid #e0e0e0;
    overflow: hidden;
}

.packlist-info-section {
    width: 300px;
    padding: 16px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: 16px;
    overflow-y: auto;
    flex-shrink: 0;
}

/* Tablet-specific adjustments (768px - 1366px) */
@media (min-width: 768px) and (max-width: 1366px) {
    .order-header.multicart-mode {
        padding: 12px;
    }

    .multicart-mode .header-items-group,
    .packlist-mode .header-items-group {
        gap: 12px;
    }

    .packlist-info-section {
        width: 260px;
        padding: 12px;
        gap: 12px;
    }
}
</style>